import { useIsNav4Enabled } from '@confluence/nav4-enabled';
import {
	SPACE_SETTINGS_DETAILS,
	SPACE_PERMISSIONS_SETTINGS,
	SPACE_SETTINGS_EXPORT,
} from '@confluence/named-routes';
import { fg } from '@confluence/feature-gating';

import { isNonNullable } from '../utils';
import { KeysOfWebItemsWithExtraUrls } from '../helpers/webItem';

import { useSpacePermissionsWebItems } from './useSpacePermissionsWebItems';
import { usePublicLinksWebItem } from './usePublicLinksWebItem';
import type {
	SpaceSettingsWebItemSectionsFragment,
	SpaceSettingsWebItemSectionsFragment_webItemSections,
} from './__types__/SpaceSettingsWebItemSectionsFragment';
import type {
	SpaceSettingsWebItemSectionsInjector,
	WebItemSectionsWithLozenge,
	WebItemSectionsWithLozenge_items,
} from './SpaceSettingsWebItemSectionsInjector';
import { useExportSpaceWebItem } from './useExportSpaceWebItem';
import { useMigratedWebItem } from './useMigratedWebItem';

export const AUTOMATION_WEBITEM_KEY =
	'com.atlassian.plugins.atlassian-connect-plugin:com.atlassian.addons.automation.confluence__confluence-space-automation-rules';
export const AUTOMATION_WEBITEM_SECTION_ID_V1 =
	'-system.space.tools-com.atlassian.addons.automation.confluence__space-automation';
export const AUTOMATION_WEBITEM_SECTION_ID_V2 = 'space-automation-section';
export const SPACE_PERMISSIONS_WEBITEM_SECTION_ID = '-system.space.tools-permissions';
export const MANAGE_SPACE_WEBITEM_SECTION_ID = '-system.space.tools-overview';
export const SPACE_DETAILS_WEBITEM_KEY = 'confluence.sections.space.tools.v2:spacedetails';
export const SPACE_DETAILS_PERSONAL_WEBITEM_KEY =
	'confluence.sections.space.tools.v2:spacedetails-personal';
export const MANAGE_CONTENT_WEBITEM_SECTION_ID = '-system.space.tools-contenttools';
export const CONTENT_MANAGER_WEBITEM_KEY = 'confluence.sections.space.tools.v2:content_manager';
export const ANALYTICS_PLUGIN_WEB_ITEM =
	'com.atlassian.plugins.atlassian-connect-plugin:com.addonengine.analytics__analytics-permissions-space-tools-tab';

const { GENERAL_PERMISSIONS_WEBITEM_COMPLETE_KEY } = KeysOfWebItemsWithExtraUrls;

export type { WebItemSectionsWithLozenge };

export const isAutomationSection = (
	section: SpaceSettingsWebItemSectionsFragment_webItemSections,
) =>
	section.id === AUTOMATION_WEBITEM_SECTION_ID_V1 ||
	section.id === AUTOMATION_WEBITEM_SECTION_ID_V2;

const useManageSpaceWebItemSection: SpaceSettingsWebItemSectionsInjector = ({
	space,
	webItemSections,
}) => {
	// XXX Don't mutate the input webItemSections! May be shared across invocations.
	const isNav4Enabled = useIsNav4Enabled();

	if (!isNav4Enabled && fg('confluence-fe-space-settings-space-details')) {
		return webItemSections
			?.filter(isNonNullable)
			.map((section): SpaceSettingsWebItemSectionsFragment_webItemSections => {
				if (section.id === MANAGE_SPACE_WEBITEM_SECTION_ID) {
					return {
						...section,
						items: section.items.map((item) => {
							// Replace the Space details item's URL from the legacy page to SPA page
							// Note: .toUrl() does not accept empty/undefined args. Only replace the URL if space data has a valid spaceKey URL param input.
							if (
								item?.completeKey?.match(
									/^confluence\.sections\.space\.tools\.v2\:spacedetails.*$/,
								) &&
								(space?.alias || space?.key)
							) {
								return {
									...item,
									url: SPACE_SETTINGS_DETAILS.toUrl({ spaceKey: space?.alias || space?.key }),
								};
							} else {
								return item;
							}
						}),
					};
				} else {
					return section;
				}
			});
	} else if (!isNav4Enabled) {
		return webItemSections;
	}

	return webItemSections
		?.filter(isNonNullable)
		.map((section): SpaceSettingsWebItemSectionsFragment_webItemSections => {
			if (section.id === MANAGE_SPACE_WEBITEM_SECTION_ID) {
				// Creates a new manage space web item section without the space details web item.
				return {
					...section,
					items: section.items.filter(
						(item) =>
							item?.completeKey !== SPACE_DETAILS_WEBITEM_KEY &&
							item?.completeKey !== SPACE_DETAILS_PERSONAL_WEBITEM_KEY,
					),
				};
			} else {
				return section;
			}
		});
};

const useManageContentWebItemSection: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	// XXX Don't mutate the input webItemSections! May be shared across invocations.

	return webItemSections
		?.filter(isNonNullable)
		.map((section): SpaceSettingsWebItemSectionsFragment_webItemSections => {
			if (section.id === MANAGE_CONTENT_WEBITEM_SECTION_ID) {
				// Creates a new manage content web item section without the content manager web item.
				return {
					...section,
					items: section.items.filter((item) => item?.completeKey !== CONTENT_MANAGER_WEBITEM_KEY),
				};
			} else {
				return section;
			}
		});
};

const useAddLozengeItem: SpaceSettingsWebItemSectionsInjector = ({ webItemSections }) => {
	return webItemSections?.map((section) => {
		if (section?.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID) {
			// XXX Don't mutate the input webItemSections! May be shared across invocations.

			const items: (WebItemSectionsWithLozenge_items | null)[] | undefined = section.items.map(
				(item) => {
					const id = item?.completeKey || item?.id;
					if (id === GENERAL_PERMISSIONS_WEBITEM_COMPLETE_KEY) {
						return {
							...item!,
							lozenge: null,
						};
					}
					return item;
				},
			);

			const sideBarLozenge = null;

			return {
				...section,
				items,
				lozenge: sideBarLozenge,
			};
		}
		return section;
	});
};

export const useAnalyticsPermissionsWebItem: SpaceSettingsWebItemSectionsInjector = ({
	space,
	webItemSections,
}) => {
	// overrides the plugin analytics permissions link with native link if it is enabled
	if (webItemSections) {
		return webItemSections.map((section) => {
			if (section && section.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID) {
				return {
					...section,
					items: section.items.map((item) => {
						if (space && item && item.completeKey === ANALYTICS_PLUGIN_WEB_ITEM) {
							const nativePermissionsURL = SPACE_PERMISSIONS_SETTINGS.toUrl({
								spaceKey: space.alias ? space.alias : space.key,
								tab: 'analytics',
							});
							return {
								...item,
								url: nativePermissionsURL,
							};
						}
						return item;
					}),
				};
			}
			return section;
		});
	}
	return webItemSections;
};

export const usePdfExportNavigationItem: SpaceSettingsWebItemSectionsInjector = ({
	space,
	webItemSections,
}) => {
	if (!space?.alias && !space?.key) return webItemSections;

	const ExportUrl = SPACE_SETTINGS_EXPORT.toUrl({ spaceKey: space.alias || space.key });

	return webItemSections?.map((section) => {
		if (section?.id === MANAGE_SPACE_WEBITEM_SECTION_ID) {
			return {
				...section,
				items: section.items.map((item) =>
					item?.completeKey === KeysOfWebItemsWithExtraUrls.EXPORT_SPACE_WEBITEM_COMPLETE_KEY
						? { ...item, url: ExportUrl }
						: item,
				),
			};
		}
		return section;
	});
};

export const useSpaceSettingsWebItemSectionsFromQuery = (
	data?: SpaceSettingsWebItemSectionsFragment,
): ReturnType<SpaceSettingsWebItemSectionsInjector> => {
	const space = data?.space;
	const injectors: SpaceSettingsWebItemSectionsInjector[] = [
		useManageSpaceWebItemSection,
		useManageContentWebItemSection,
		usePublicLinksWebItem,
		useSpacePermissionsWebItems,
		useAddLozengeItem,
		useExportSpaceWebItem,
		useMigratedWebItem,
		useAnalyticsPermissionsWebItem,
		...(fg('confluence-fe-space-settings-enable-afm-pdf') ? [usePdfExportNavigationItem] : []),
	];

	return injectors.reduce(
		(webItemSections, injector) => injector({ space, webItemSections }),
		data?.webItemSections,
	);
};
